import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import deleteOrder from "@insite/client-framework/Store/Pages/SavedOrderDetails/Handlers/DeleteOrder";
import translate from "@insite/client-framework/Translate";
import ProductAvailability, { ProductAvailabilityStyles } from "@tSurya/Overrides/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal/Modal";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import BrandBadge from "@tSurya/Components/BrandBadge";
import PrintBrandBadge from "@tSurya/Components/PrintBrandBadge";
import { normalizeSourceBrand } from "@tSurya/Common/Utilities/websiteUtils";
import removeSavedOrderCartLine from "@tSurya/Handlers/RemoveSavedOrderCartLine";
import CustomHeartIcon from "@tSurya/Icons/CustomHeartIcon";
import ProductDescription, { ProductDescriptionStyles } from "@tSurya/Overrides/Components/ProductDescription";
import { MGBW_SHIPPING_AVAILABILITY, MGBW_VALIDATION_MESSAGES, MGBW_VALIDATION_STATUS, MgbwValidationStatus } from "@tSurya/Common/MgbwPropertyKeys";
import { useVisibleMgbwSelections } from "@tSurya/Common/Hooks/useMgbwSelections";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    extendedStyles?: SavedOrderDetailsCartLineStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    allowMultipleWishLists: getSettingsCollection(state).wishListSettings.allowMultipleWishLists,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    savedOrderListPageLink: getPageLinkByPageType(state, "SavedOrderListPage")?.url,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
    removeSavedOrderCartLine,
    deleteOrder,
};

type Props = OwnProps & HasCartLineContext & HasToasterContext & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderDetailsCartLineStyles {
    productPrice?: ProductPriceStyles;
    cartLineQuantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    badge?: InjectableCss;
    savedOrderLineInfoGridItem?: GridItemProps;
    savedOrderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    productAvailabilityGridItem?: GridItemProps;
    productAvailability?: ProductAvailabilityStyles;
    mgbwShippingAvailabilityText?: TypographyPresentationProps;
    configurationDetails?: InjectableCss;
    configurationSummary?: TypographyPresentationProps;
    configurationLabel?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    inactiveProductLabel?: TypographyPresentationProps;
    priceGridItem?: GridItemProps;
    quantityGridItem?: GridItemProps;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    savedOrderLineCardAddToListGridItem?: GridItemProps;
    savedOrderLineCardAddToListButton?: ButtonPresentationProps;
    heartIcon?: IconPresentationProps;
    buttonStyle?: ButtonPresentationProps;
    modalContainer?: ModalPresentationProps;
    modalBodyContainer?: GridContainerProps;
    closeButton?: ButtonPresentationProps;
    modalContent?: TypographyPresentationProps;
    modalYesButton?: ButtonPresentationProps;
    serviceLabel?: InjectableCss;
}

export const savedOrderDetailsCartLineStyles: SavedOrderDetailsCartLineStyles = {
    heartIcon: {
        size: 15,
    },
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;

            @media print {
                padding: 14px 0;
                page-break-inside: avoid;
                break-inside: avoid;
            }
        `,
        offsetCss: css`
            display: grid !important;
            grid-template-columns: 135px minmax(0, 1fr);

            > div {
                width: 100% !important;
                max-width: 100% !important;
                flex-basis: auto !important;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            grid-template-columns: 82px minmax(0, 1fr);
                        `,
                    ],
                    "max",
                )}

            @media print {
                grid-template-columns: 106px minmax(0, 1fr);
                align-items: start;
                column-gap: 10px;
            }

            @supports (-webkit-touch-callout: none) {
                @media print and (hover: none) and (pointer: coarse) {
                    display: flex !important;
                    flex-direction: row;
                    align-items: flex-start;
                    gap: 10px;
                }
            }
        `,
    },
    productImageGridItem: {
        width: 12,
        printWidth: 1,
        css: css`
            flex-direction: column;
            align-items: flex-start;

            @media print {
                max-width: 106px;
                padding: 0;
                page-break-inside: avoid;
                break-inside: avoid;
            }

            @supports (-webkit-touch-callout: none) {
                @media print and (hover: none) and (pointer: coarse) {
                    width: 106px !important;
                    min-width: 106px !important;
                    max-width: 106px !important;
                    flex: 0 0 106px !important;
                }
            }
        `,
    },
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 115px;
                max-width: 115px;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                width: 62px;
                                max-width: 62px;
                            `,
                        ],
                        "max",
                    )}

                @media print {
                    width: 96px;
                    max-width: 96px;
                }
            `,
        },
        img: {
            css: css`
                display: block;
                width: 100%;
                max-width: 100%;
                height: auto;
                object-fit: contain;

                /* Clamp the image itself so the brand badge below it can never overflow
                   the print row and paint over the next line. */
                @media print {
                    max-height: 96px;
                }
            `,
        },
    },
    badge: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 115px;
            margin-top: 8px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            width: 62px;
                        `,
                    ],
                    "max",
                )}

            @media print {
                width: 96px;
                margin-top: 6px;
                overflow: hidden;
            }
        `,
    },
    savedOrderLineInfoGridItem: {
        width: 12,
        printWidth: 11,
        css: css`
            @supports (-webkit-touch-callout: none) {
                @media print and (hover: none) and (pointer: coarse) {
                    width: auto !important;
                    max-width: none !important;
                    flex: 1 1 auto !important;
                    min-width: 0;
                }
            }
        `,
    },
    savedOrderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 8,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    productAvailabilityGridItem: {
        width: 12,
    },
    mgbwShippingAvailabilityText: {
        css: css`
            display: block;
            margin-top: 8px;
            color: #545454;
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: 500;
            line-height: 1.4;
        `,
    },
    configurationDetails: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 4px;
        `,
    },
    configurationSummary: {
        color: "#545454",
        css: css`
            font-size: ${TYPOGRAPHY.size.base};
            font-weight: ${TYPOGRAPHY.weight.base};
            line-height: 1.5;
        `,
    },
    configurationLabel: {
        color: "#545454",
        css: css`
            font-weight: ${TYPOGRAPHY.weight.bold};
            text-transform: capitalize;
        `,
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 7,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 12, 12, 4, 4],
        printWidth: 4,
        css: css`
            flex-direction: column;
        `,
    },
    productPrice: {
        price: {
            priceText: {
                weight: "normal",
            },
        },
        secondaryPrice: {
            priceText: {
                weight: "normal",
            },
        },
    },
    inactiveProductLabel: {
        color: "danger",
    },
    quantityGridItem: {
        width: [2, 2, 2, 1, 1],
    },
    extendedUnitNetPriceGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            flex-direction: column;
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
        heading: {
            css: css`
                text-transform: capitalize;
            `,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
            `,
        },
        heading: {
            css: css`
                text-transform: capitalize;
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
            `,
        },
    },
    savedOrderLineCardAddToListGridItem: {
        width: [6, 6, 6, 4, 4],
        printWidth: 0,
    },
    buttonStyle: {
        css: css`
            display: grid;
            grid-row-gap: 10px;
        `,
    },
    modalContainer: {
        size: 450,
        cssOverrides: {
            modalTitle: css`
                border: 0px;
                padding: 10px 20px;
                background-color: #f8f8f8;
            `,
            modalContent: css`
                padding: 15px 20px;
            `,
            modalBody: css`
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 95%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        css`
                            width: 75%;
                            height: auto;
                        `,
                        null,
                        null,
                    ])}
            `,
        },
    },
    modalBodyContainer: {
        css: css`
            display: flex;
            padding: 16px;
            margin-top: 15px;
        `,
        offsetCss: css`
            justify-content: right;
        `,
    },
    closeButton: {
        css: css`
            span[class^="TypographyStyle"] {
                font-size: 14px;
            }
        `,
    },
    modalYesButton: {
        buttonType: "outline",
        css: css`
            margin-right: 10px;
            span[class^="TypographyStyle"] {
                color: black;
                font-size: 14px;
            }
        `,
    },
    modalContent: {
        css: css`
            line-height: 24px;
            font-size: 15px;
            font-weight: 400;
            font-family: Mulish;
        `,
    },
    serviceLabel: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.title};
            font-style: italic;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.smaller};
            line-height: ${TYPOGRAPHY.lineHeight.base};
            color: ${getColor("text.main")};
        `,
    },
};

// The MGBW badge SVG does not survive the browser print pipeline, so print swaps in the
// CSS/text wordmark used by the projects print view.
const ScreenOnlyBadge = styled.div`
    width: 100%;

    @media print {
        display: none;
    }
`;

const PrintOnlyBadge = styled.div`
    display: none;

    @media print {
        display: block;
    }
`;

const getValidationMessagesFromCartLine = (cartLine: { properties?: Record<string, string> }): string[] => {
    const raw = cartLine.properties?.[MGBW_VALIDATION_MESSAGES];
    if (!raw) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.filter((message): message is string => typeof message === "string") : [];
    } catch {
        return [raw];
    }
};

const SavedOrderDetailsCartLine: React.FC<Props> = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    extendedStyles,
    showPricing,
    allowMultipleWishLists,
    enableVat,
    vatPriceDisplay,
    setAddToListModalIsOpen,
    addToWishList,
    toaster,
    removeSavedOrderCartLine,
    deleteOrder,
    savedOrderListPageLink,
}) => {
    const [styles] = React.useState(() => mergeToNew(savedOrderDetailsCartLineStyles, extendedStyles));
    const [isOpen, setIsOpen] = React.useState(false);
    const history = useHistory();
    const visibleSelections = useVisibleMgbwSelections(cartLine.properties);
    const validationStatus = cartLine.properties?.[MGBW_VALIDATION_STATUS];
    const isInvalidConfiguration = validationStatus === MgbwValidationStatus.stale || validationStatus === MgbwValidationStatus.unconfigured;
    const validationMessages = getValidationMessagesFromCartLine(cartLine);
    const normalizedSourceBrand = normalizeSourceBrand(cartLine.properties?.sourceBrand);
    const isGlobalViewsSourceBrand = normalizedSourceBrand === "globalviews";
    const isMgbwSourceBrand = normalizedSourceBrand === "mg+bw" || normalizedSourceBrand === "mgbw" || normalizedSourceBrand === "mitchellgold+bobwilliams";
    const usesBrandBadgeAsIdentity = isGlobalViewsSourceBrand || isMgbwSourceBrand;
    const mgbwShippingAvailability = cartLine.properties?.[MGBW_SHIPPING_AVAILABILITY];
    const renderProductAvailability = () => {
        if (mgbwShippingAvailability) {
            return <Typography {...styles.mgbwShippingAvailabilityText}>{mgbwShippingAvailability}</Typography>;
        }

        if (cartLine.quoteRequired) {
            return null;
        }

        return (
            <ProductAvailability
                productId={cartLine.productId!}
                availability={cartLine.availability!}
                unitOfMeasure={cartLine.unitOfMeasure}
                trackInventory={cartLine.trackInventory}
                isSwatch={cartLine.properties?.mgbw_isSwatch === "true"}
                failedToLoadInventory={cart.failedToGetRealTimeInventory}
                extendedStyles={styles.productAvailability}
            />
        );
    };
    const addToListClickHandler = () => {
        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure ?? "",
            properties: { ...(cartLine.properties || {}) },
        };

        if (!allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toaster.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC

                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toaster.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };
    const handleSavedOrderDelete = (cartLineId: string, cartId: string) => {
        const isRugLinked = cart.cartLines?.some(x => x.properties.linkedRugItemCode === cartLine.productName);
        if (isRugLinked || cartLine.properties.isRollpadService === "true") {
            setIsOpen(true);
        } else {
            removeSavedOrderCartLineHandler(cartLineId, cartId);
        }
    };
    const deleteCartHandler = (cartLineId: string, cartId: string) => {
        setIsOpen(false);
        removeSavedOrderCartLineHandler(cartLineId, cartId);
    };
    const removeSavedOrderCartLineHandler = (cartLineId: string, cartId: string) => {
        if (!cart.cartLines || cart.cartLines.length === 0) {
            return;
        }
        const itemsToRemove: string[] = [];
        itemsToRemove.push(cartLineId);

        cart.cartLines.forEach(element => {
            if (element.properties.isRollpadService === "true" && element.properties.linkedRugItemCode === cartLine.productName) {
                itemsToRemove.push(element.id);
            }
        });

        if (itemsToRemove.length === cart.cartLines.length) {
            deleteOrder({
                onSuccess: () => {
                    toaster.addToast({ body: translate("Order Deleted"), messageType: "success" });
                    history.push(savedOrderListPageLink!);
                },
            });
        } else {
            removeSavedOrderCartLine({
                cartLineIds: itemsToRemove,
                cartId,
                onSuccess: () => {
                    const itemLabel = itemsToRemove.length === 1 ? translate("Item Deleted") : translate("Items Deleted");
                    toaster.addToast({ body: itemLabel, messageType: "success" });
                },
            });
        }
    };

    return (
        <GridContainer {...styles.container} data-test-selector={`savedOrderDetails_orderLine_${cartLine.id}`}>
            <GridItem {...styles.productImageGridItem}>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
                <StyledWrapper {...styles.badge}>
                    <ScreenOnlyBadge>
                        <BrandBadge brandName={cartLine.properties?.sourceBrand} />
                    </ScreenOnlyBadge>
                    <PrintOnlyBadge>
                        <PrintBrandBadge brandName={cartLine.properties?.sourceBrand} />
                    </PrintOnlyBadge>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.savedOrderLineInfoGridItem}>
                <GridContainer {...styles.savedOrderLineInfoContainer}>
                    <GridItem {...styles.productDescriptionAndPartNumbersGridItem}>
                        <GridContainer {...styles.productDescriptionAndPartNumbersContainer}>
                            <GridItem {...styles.productBrandAndDescriptionGridItem}>
                                {cartLine.brand && !usesBrandBadgeAsIdentity && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                                {visibleSelections.length > 0 && (
                                    <StyledWrapper {...styles.configurationDetails}>
                                        {visibleSelections.map(entry => (
                                            <Typography {...styles.configurationSummary} key={entry.OptionId}>
                                                <Typography {...styles.configurationLabel} as="span">
                                                    {entry.Name}:
                                                </Typography>{" "}
                                                {entry.Value}
                                            </Typography>
                                        ))}
                                    </StyledWrapper>
                                )}
                                {isInvalidConfiguration && (
                                    <StyledWrapper
                                        css={css`
                                            display: flex;
                                            flex-direction: column;
                                            gap: 4px;
                                            margin-top: 8px;
                                        `}
                                    >
                                        <Typography
                                            css={css`
                                                color: #c44b25;
                                                font-weight: ${TYPOGRAPHY.weight.baseBold};
                                            `}
                                        >
                                            {validationMessages[0] ?? translate("This configuration is no longer available.")}
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </GridItem>
                            {!usesBrandBadgeAsIdentity && (
                                <GridItem {...styles.productPartNumbersGridItem}>
                                    <ProductPartNumbers
                                        productNumber={cartLine.erpNumber}
                                        customerProductNumber={cartLine.customerName}
                                        manufacturerItem={cartLine.manufacturerItem}
                                        extendedStyles={styles.productPartNumbers}
                                    />
                                </GridItem>
                            )}
                            {cartLine.properties.isRollpadService === "true" && (
                                <GridItem {...styles.productPartNumbersGridItem}>
                                    <StyledWrapper {...styles.serviceLabel}>Roll Pad Service</StyledWrapper>
                                </GridItem>
                            )}
                            <GridItem {...styles.productAvailabilityGridItem}>{renderProductAvailability()}</GridItem>
                            {cartLine.isRestricted && (
                                <GridItem {...styles.productAvailabilityGridItem}>
                                    <Typography {...styles.cartLineErrorMessageText}>{translate("Restricted product")}</Typography>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem {...styles.productPriceAndQuantityGridItem}>
                        <GridContainer {...styles.productPriceAndQuantityContainer}>
                            <GridItem {...styles.priceGridItem}>
                                <ProductPrice
                                    product={cartLine}
                                    showSavings={true}
                                    showSavingsAmount={showSavingsAmount}
                                    showSavingsPercent={showSavingsPercent}
                                    extendedStyles={styles.productPrice}
                                />
                                {!cartLine.isActive && (
                                    <Typography {...styles.inactiveProductLabel} data-test-selector="savedOrderDetails_inactiveProduct">
                                        {translate("Inactive Product")}
                                    </Typography>
                                )}
                            </GridItem>
                            <GridItem {...styles.quantityGridItem}>
                                <CartLineQuantity cart={cart} editable={false} extendedStyles={styles.cartLineQuantity} />
                            </GridItem>
                            <GridItem {...styles.extendedUnitNetPriceGridItem}>
                                {cartLine.pricing && showPricing && (
                                    <>
                                        {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                            <>
                                                <SmallHeadingAndText
                                                    heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                                    text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    extendedStyles={styles.subtotalWithoutVatText}
                                                />
                                                <SmallHeadingAndText
                                                    heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                                    text={cartLine.pricing.vatAmountDisplay}
                                                    extendedStyles={styles.vatAmount}
                                                />
                                            </>
                                        )}
                                        <SmallHeadingAndText
                                            heading={
                                                !enableVat
                                                    ? translate("Subtotal")
                                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                                      ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                      : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                            }
                                            text={
                                                enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                                    : cartLine.pricing.extendedUnitNetPriceDisplay
                                            }
                                            extendedStyles={styles.extendedUnitNetPrice}
                                        />
                                    </>
                                )}
                            </GridItem>
                            <GridItem {...styles.savedOrderLineCardAddToListGridItem}>
                                {cartLine.canAddToWishlist && !cartLine.isRestricted && (
                                    <StyledWrapper {...styles.buttonStyle}>
                                        <Button
                                            {...styles.savedOrderLineCardAddToListButton}
                                            data-test-selector={`savedOrderDetails_productLine_${cartLine.erpNumber}`}
                                            onClick={addToListClickHandler}
                                        >
                                            <Icon src={CustomHeartIcon} {...styles.heartIcon}></Icon>
                                            {translate("Add to Project")}
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                handleSavedOrderDelete(cartLine.id, cart.id);
                                            }}
                                            variant="secondary"
                                            data-test-selector={`savedOrderDetails_delete_${cartLine.id}`}
                                            css={css`
                                                background: transparent;
                                                border: 2px solid ${COLORS.black};
                                                border-radius: 0;
                                                color: ${COLORS.black};
                                                height: 40px;
                                                min-height: 40px;
                                                padding: 0 21px;
                                                box-shadow: none;
                                                transition: all 0.1s ease-out;
                                                span[class^="TypographyStyle"] {
                                                    color: ${COLORS.black};
                                                }
                                                &:hover {
                                                    background: transparent;
                                                    border: 2px solid ${COLORS.black};
                                                }
                                            `}
                                        >
                                            {translate("Delete")}
                                        </Button>
                                    </StyledWrapper>
                                )}
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </GridItem>
            <Modal {...styles.modalContainer} isOpen={isOpen} isCloseable={false} sizeVariant="small" headline={translate("Alert!")}>
                <Typography {...styles.modalContent}>{siteMessage("Rugpad_DeleteConfirmation")}</Typography>
                <GridContainer {...styles.modalBodyContainer}>
                    <Button {...styles.modalYesButton} onClick={() => deleteCartHandler(cartLine.id, cart.id)}>
                        Yes
                    </Button>
                    <Button {...styles.closeButton} onClick={() => setIsOpen(false)}>
                        {siteMessage("Cancel")}
                    </Button>
                </GridContainer>
            </Modal>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withCartLine(SavedOrderDetailsCartLine)));
